import { Link, useNavigate } from "react-router-dom";
import { Lock, Download, Linkedin, Youtube, ShieldCheck } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { trackedDownloadUrl } from "@/lib/downloadTracking";
import { openCookieSettings } from "@/lib/consent";
import { ClinicalTrialBanner } from "@/components/ClinicalTrialBanner";


const translations = {
  de: {
    description: "Die neue Generation von Abutments! Patientenindividuell – Schnell, einfach & sicher.",
    smarter: "SMARTER.",
    navigation: "Navigation",
    product: "Produkt",
    advantages: "Vorteile",
    faq: "FAQ",
    aiPress: "AI & Press",
    research: "Studien",
    contact: "Kontakt",
    legal: "Rechtliches",
    imprint: "Impressum",
    privacy: "Datenschutz",
    terms: "AGB",
    ifu: "IFU",
    technicalGuide: "Leitfaden Technikerzettel",
    cookieSettings: "Cookie-Einstellungen",
    copyright: "Alle Rechte vorbehalten.",
  },
  en: {
    description: "The new generation of abutments! Patient-specific – Fast, simple & safe.",
    smarter: "SMARTER.",
    navigation: "Navigation",
    product: "Product",
    advantages: "Advantages",
    faq: "FAQ",
    aiPress: "AI & Press",
    research: "Research",
    contact: "Contact",
    legal: "Legal",
    imprint: "Imprint",
    privacy: "Privacy",
    terms: "Terms",
    ifu: "IFU",
    technicalGuide: "Technical Guide",
    cookieSettings: "Cookie settings",
    copyright: "All rights reserved.",
  },
  fr: {
    description: "La nouvelle génération de piliers! Spécifique au patient – Rapide, simple et sûr.",
    smarter: "PLUS INTELLIGENT.",
    navigation: "Navigation",
    product: "Produit",
    advantages: "Avantages",
    faq: "FAQ",
    aiPress: "AI & Press",
    research: "Études",
    contact: "Contact",
    legal: "Légal",
    imprint: "Mentions légales",
    privacy: "Confidentialité",
    terms: "CGV",
    ifu: "IFU",
    technicalGuide: "Guide technique",
    cookieSettings: "Paramètres des cookies",
    copyright: "Tous droits réservés.",
  },
  nl: {
    description: "De nieuwe generatie abutments! Patiëntspecifiek – Snel, eenvoudig en veilig.",
    smarter: "SLIMMER.",
    navigation: "Navigatie",
    product: "Product",
    advantages: "Voordelen",
    faq: "FAQ",
    aiPress: "AI & Press",
    research: "Onderzoek",
    contact: "Contact",
    legal: "Juridisch",
    imprint: "Impressum",
    privacy: "Privacy",
    terms: "Voorwaarden",
    ifu: "IFU",
    technicalGuide: "Technische gids",
    cookieSettings: "Cookie-instellingen",
    copyright: "Alle rechten voorbehouden.",
  },
  it: {
    description: "La nuova generazione di abutment! Specifico per il paziente – Veloce, semplice e sicuro.",
    smarter: "PIÙ INTELLIGENTE.",
    navigation: "Navigazione",
    product: "Prodotto",
    advantages: "Vantaggi",
    faq: "FAQ",
    aiPress: "AI & Press",
    research: "Studi",
    contact: "Contatto",
    legal: "Legale",
    imprint: "Colophon",
    privacy: "Privacy",
    terms: "Termini",
    ifu: "IFU",
    technicalGuide: "Guida tecnica",
    cookieSettings: "Impostazioni cookie",
    copyright: "Tutti i diritti riservati.",
  },
  es: {
    description: "¡La nueva generación de pilares! Específico para el paciente – Rápido, simple y seguro.",
    smarter: "MÁS INTELIGENTE.",
    navigation: "Navegación",
    product: "Producto",
    advantages: "Ventajas",
    faq: "FAQ",
    aiPress: "AI & Press",
    research: "Estudios",
    contact: "Contacto",
    legal: "Legal",
    imprint: "Aviso legal",
    privacy: "Privacidad",
    terms: "Términos",
    ifu: "IFU",
    technicalGuide: "Guía técnica",
    cookieSettings: "Configuración de cookies",
    copyright: "Todos los derechos reservados.",
  },
};

export const Footer = () => {
  const { language } = useLanguage();
  const t = translations[language];
  const currentYear = new Date().getFullYear();
  const navigate = useNavigate();

  const scrollToContact = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    const contactSection = document.getElementById('kontakt');
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <>
      <ClinicalTrialBanner variant="inline" />
      <footer className="bg-secondary/50 border-t border-border py-12">
        <div className="container mx-auto px-4">
        <div className="grid md:grid-cols-4 gap-8 mb-8">
          {/* Company Info */}
          <div className="md:col-span-2">
            <div className="flex items-center space-x-3 mb-4">
              <img 
                src="/images/logo-eap-full.png" 
                alt="EAP Abutments Logo" 
                className="h-16 w-auto"
              />
            </div>
            <p className="text-muted-foreground mb-4 max-w-md">
              {t.description} <span className="font-bold">{t.smarter}</span>
            </p>
            <div className="flex gap-4 items-center">
              <a 
                href="https://www.linkedin.com/company/eap-produktions-und-patentverwertungs-gmbh/?viewAsMember=true" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-muted-foreground hover:text-primary transition-colors"
                aria-label="LinkedIn"
              >
                <Linkedin className="w-5 h-5" />
              </a>
              <a 
                href="https://www.youtube.com/@eapabutments" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-muted-foreground hover:text-primary transition-colors"
                aria-label="YouTube"
              >
                <Youtube className="w-5 h-5" />
              </a>
              <div className="flex items-center gap-2 text-muted-foreground">
                <ShieldCheck className="w-5 h-5" />
                <span className="text-sm font-semibold">ISO 27001 READY</span>
              </div>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="font-semibold text-foreground mb-4">{t.navigation}</h3>
            <ul className="space-y-2">
              <li>
                <a href="/#produkt" className="text-muted-foreground hover:text-primary transition-colors">
                  {t.product}
                </a>
              </li>
              <li>
                <a href="/#vorteile" className="text-muted-foreground hover:text-primary transition-colors">
                  {t.advantages}
                </a>
              </li>
              <li>
                <Link to="/faq" className="text-muted-foreground hover:text-primary transition-colors" onClick={() => window.scrollTo(0, 0)}>
                  {t.faq}
                </Link>
              </li>
              <li>
                <Link to="/ai-press" className="text-muted-foreground hover:text-primary transition-colors" onClick={() => window.scrollTo(0, 0)}>
                  {t.aiPress}
                </Link>
              </li>
              <li>
                <Link to="/research" className="text-muted-foreground hover:text-primary transition-colors" onClick={() => window.scrollTo(0, 0)}>
                  {t.research}
                </Link>
              </li>
              <li>
                <a 
                  href="/#kontakt" 
                  onClick={scrollToContact}
                  className="text-muted-foreground hover:text-primary transition-colors cursor-pointer"
                >
                  {t.contact}
                </a>
              </li>
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h3 className="font-semibold text-foreground mb-4">{t.legal}</h3>
            <ul className="space-y-2">
              <li>
                <Link to="/impressum" className="text-muted-foreground hover:text-primary transition-colors" onClick={() => window.scrollTo(0, 0)}>
                  {t.imprint}
                </Link>
              </li>
              <li>
                <Link to="/datenschutz" className="text-muted-foreground hover:text-primary transition-colors" onClick={() => window.scrollTo(0, 0)}>
                  {t.privacy}
                </Link>
              </li>
              <li>
                <Link to="/agb" className="text-muted-foreground hover:text-primary transition-colors" onClick={() => window.scrollTo(0, 0)}>
                  {t.terms}
                </Link>
              </li>
              <li>
                <Link 
                  to="/ifu" 
                  className="text-muted-foreground hover:text-primary transition-colors"
                  onClick={() => window.scrollTo(0, 0)}
                >
                  {t.ifu}
                </Link>
              </li>
              <li>
                <a 
                  href={trackedDownloadUrl("leitfaden-technikerzettel", language)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-muted-foreground hover:text-primary transition-colors inline-flex items-center gap-1"
                >
                  <Download className="w-3 h-3" />
                  {t.technicalGuide}
                </a>
              </li>
              <li>
                <button 
                  onClick={openCookieSettings}
                  className="text-muted-foreground hover:text-primary transition-colors text-left"
                >
                  {t.cookieSettings}
                </button>
              </li>
            </ul>
          </div>
        </div>

        <div className="pt-8 border-t border-border">
          <div className="text-center text-muted-foreground relative">
            <p>&copy; 2017-{currentYear} EAP® Abutments. {t.copyright}</p>
            
            {/* Hidden Admin Login - bottom right (a11y: excluded from screenreaders & tab order) */}
            <button
              onClick={() => navigate("/auth")}
              className="fixed bottom-4 right-4 p-2 md:p-2 opacity-30 hover:opacity-70 md:opacity-10 md:hover:opacity-40 transition-opacity duration-300 z-50"
              aria-hidden="true"
              tabIndex={-1}
            >
              <Lock className="w-5 h-5 md:w-4 md:h-4 text-muted-foreground" />
            </button>
          </div>
        </div>
      </div>
      </footer>
    </>
  );
};
